<template>
	<div class="contentBlock marginBottomSmaller">
		<!-- Header -->
		<div class="contentColumnIgnoreMobile">Sale Price</div>
		<div class="contentColumnIgnoreMobile">Commission</div>

		<!-- Staff View -->
		<template v-if="isStaff">
			<template v-if="!isEditable()">
				<!-- Read-only view for staff after sending/signing -->
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						$1 &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss1b }}
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss1c) }}%
					</span>
				</div>

				<!-- Hidden second tier -->
				<div
					class="hiddenTier"
					:class="{ hidden: !showSecondTier }"
				>
					<div class="contentColumnIgnoreMobile">
						<span class="disabledValue plainFont contentBlock">
							${{ contract.ss2a }} &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss2b }}
						</span>
					</div>
					<div
						id="ss2cContainer"
						class="contentColumnIgnoreMobile"
					>
						<span class="disabledValue plainFont contentBlock">
							{{ formatCommission(contract.ss2c) }}%
						</span>
					</div>
				</div>

				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						${{ displaySs3a }} &nbsp;&nbsp;and above
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss3c) }}%
					</span>
				</div>
			</template>
			<template v-else>
				<!-- Editable view for staff -->
				<div class="contentColumnIgnoreMobile">
					$
					<input
						class="hiddenInput plainFont textInputSmall textCenter"
						type="text"
						value="1"
						disabled
						name="ss1a"
					/>
					&nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;
					<input
						v-model="contract.ss1b"
						class="hiddenInput plainFont textInputSmall textCenter"
						type="text"
						name="ss1b"
						@change="updateSs2a"
					/>
				</div>
				<div class="contentColumnIgnoreMobile">
					<input
						:value="formatCommission(contract.ss1c)"
						class="hiddenInput plainFont textInputSmall textCenter"
						type="text"
						name="ss1c"
						@input="(e) => (contract.ss1c = e.target.value)"
					/>
					%
				</div>

				<!-- Hidden second tier -->
				<div
					class="hiddenTier"
					:class="{ hidden: !showSecondTier }"
				>
					<div class="contentColumnIgnoreMobile">
						$
						<input
							class="hiddenInput plainFont textInputSmall textCenter"
							type="text"
							:value="contract.ss2a"
							disabled
							name="ss2a"
						/>
						&nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;
						<input
							v-model="contract.ss2b"
							class="hiddenInput plainFont textInputSmall textCenter"
							type="text"
							name="ss2b"
							@change="updateSs3a"
						/>
					</div>
					<div class="contentColumnIgnoreMobile">
						<input
							:value="formatCommission(contract.ss2c)"
							class="hiddenInput plainFont textInputSmall textCenter"
							type="text"
							name="ss2c"
							@input="(e) => (contract.ss2c = e.target.value)"
						/>
						%
					</div>
				</div>

				<!-- Third tier -->
				<template v-if="showSecondTier">
					<div class="contentColumnIgnoreMobile">
						$
						<span class="plainFont contentBlock textInputSmall textCenter">
							{{ displaySs3a }}
						</span>
						<span
							class="clickable"
							@click="toggleHiddenTier"
						>
							&nbsp;&nbsp;and above
						</span>
					</div>
					<div class="contentColumnIgnoreMobile">
						<input
							:value="formatCommission(contract.ss3c)"
							class="hiddenInput plainFont textInputSmall textCenter"
							type="text"
							name="ss3a"
							@input="(e) => (contract.ss3c = e.target.value)"
						/>
						%
					</div>
				</template>
				<template v-else>
					<div class="contentColumnIgnoreMobile">
						$
						<span class="plainFont contentBlock textInputSmall textCenter">
							{{ contract.ss2a }}
						</span>
						<span
							class="clickable"
							@click="toggleHiddenTier"
						>
							&nbsp;&nbsp;and above
						</span>
					</div>
					<div class="contentColumnIgnoreMobile">
						<input
							:value="formatCommission(contract.ss2c)"
							class="hiddenInput plainFont textInputSmall textCenter"
							type="text"
							name="ss3c"
							@input="(e) => (contract.ss2c = e.target.value)"
						/>
						%
					</div>
				</template>
			</template>
		</template>

		<!-- Customer View -->
		<template v-else>
			<!-- Show collapsed view if applicable -->
			<template v-if="shouldCollapseLastTwo">
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						$1 &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss1b }}
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss1c) }}%
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						${{ contract.ss2a }} &nbsp;&nbsp;and above
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss2c) }}%
					</span>
				</div>
			</template>
			<template v-else-if="shouldCollapseFirstTwo">
				<!-- First two tiers collapsed -->
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						$1 &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss2b }}
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss2c) }}%
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						${{ displaySs3a }}&nbsp;&nbsp;and above
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss3c) }}%
					</span>
				</div>
			</template>
			<template v-else>
				<!-- No collapsing -->
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						$1 &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss1b }}
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss1c) }}%
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						${{ contract.ss2a }} &nbsp;&nbsp;&nbsp;through&nbsp;&nbsp;&nbsp;${{ contract.ss2b }}
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss2c) }}%
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						${{ displaySs3a }} &nbsp;&nbsp;and above
					</span>
				</div>
				<div class="contentColumnIgnoreMobile">
					<span class="disabledValue plainFont contentBlock">
						{{ formatCommission(contract.ss3c) }}%
					</span>
				</div>
			</template>
		</template>
	</div>
</template>

<script>
	import contractState from '@/mixins/contractState'

	export default {
		name: 'CommissionRates',
		mixins: [contractState],
		props: {
			contract: {
				type: Object,
				required: true
			}
		},
		data() {
			return {
				showSecondTier: false
			}
		},
		computed: {
			/**
			 * Whether the last two tiers should be collapsed
			 * @returns {boolean} True if the last two tiers should be collapsed, false otherwise
			 */
			shouldCollapseLastTwo() {
				return (
					Number(this.contract.ss2a) + 1 === Number(this.contract.ss2b) &&
					this.contract.ss2c === this.contract.ss3c
				)
			},
			/**
			 * Whether the first and second tiers should be collapsed
			 * @returns {boolean} True if the first and second tiers should be collapsed, false otherwise
			 */
			shouldCollapseFirstTwo() {
				return (
					Number(this.contract.ss1b) + 1 === Number(this.contract.ss2a) &&
					this.contract.ss1c === this.contract.ss2c
				)
			},
			/**
			 * Whether the second tier should be collapsed
			 * @returns {boolean} True if the second tier should be collapsed, false otherwise
			 */
			shouldCollapseSecondTier() {
				return (
					Number(this.contract.ss2a) + 1 === Number(this.contract.ss2b) &&
					this.contract.ss2c === this.contract.ss3c
				)
			},
			/**
			 * The third tier start value
			 * @returns {number} The third tier start value
			 */
			displaySs3a() {
				if (this.shouldCollapseSecondTier) {
					return this.contract.ss2a + 1
				}
				return this.contract.ss3a
			},
			/**
			 * Whether the second tier should be shown based on contract values
			 * @returns {boolean} True if the second tier should be shown
			 */
			shouldShowSecondTier() {
				// Show second tier if:
				// 1. The tiers are not collapsed
				// 2. The values indicate a three-tier structure
				return !(
					this.shouldCollapseLastTwo ||
					this.shouldCollapseFirstTwo ||
					this.shouldCollapseSecondTier ||
					(Number(this.contract.ss2b) === Number(this.contract.ss2a) + 1 &&
						this.contract.ss2c === this.contract.ss3c)
				)
			}
		},
		watch: {
			// Watch for changes in contract commission values
			'contract.ss1b': 'updateShowSecondTier',
			'contract.ss2a': 'updateShowSecondTier',
			'contract.ss2b': 'updateShowSecondTier',
			'contract.ss2c': 'updateShowSecondTier',
			'contract.ss3c': 'updateShowSecondTier'
		},
		mounted() {
			// Initialize showSecondTier based on contract values
			this.showSecondTier = this.contract.ss2b > this.contract.ss2a + 1
		},
		methods: {
			/**
			 * Format the commission value
			 * @param {number} value - The commission value
			 * @returns {string} The formatted commission value
			 */
			formatCommission(value) {
				return value?.toString().replace('.00', '') || ''
			},
			/**
			 * Reset third tier values to match second tier when in two-tier mode
			 */
			resetThirdTier() {
				if (!this.showSecondTier) {
					this.contract.ss3a = Number(this.contract.ss2b) + 1
					this.contract.ss3c = this.contract.ss2c
				}
			},
			/**
			 * Update the second tier start value
			 */
			updateSs2a() {
				if (!this.showSecondTier) {
					this.contract.ss2a = Number(this.contract.ss1b) + 1
					this.contract.ss2b = Number(this.contract.ss1b) + 2
					this.contract.ss3a = Number(this.contract.ss2b) + 1
					this.contract.ss3c = this.contract.ss2c
				} else {
					this.contract.ss2a = Number(this.contract.ss1b) + 1
					if (Number(this.contract.ss2b) <= Number(this.contract.ss2a)) {
						this.contract.ss2b = Number(this.contract.ss2a) + 1
						this.contract.ss3a = Number(this.contract.ss2b) + 1
					}
				}
			},
			/**
			 * Update the third tier start value
			 */
			updateSs3a() {
				if (!this.showSecondTier) {
					this.resetThirdTier()
				} else {
					this.contract.ss3a = Number(this.contract.ss2b) + 1
				}
			},
			/**
			 * Update the third tier commission value
			 */
			updateSs2c() {
				if (!this.showSecondTier) {
					this.contract.ss3c = this.contract.ss2c
				}
			},
			/**
			 * Toggle the hidden tier
			 */
			toggleHiddenTier() {
				this.showSecondTier = !this.showSecondTier
				if (!this.showSecondTier) {
					// Reset commission rates when hiding
					this.contract.ss1a = 1
					this.contract.ss1b = 999
					this.contract.ss1c = 30
					this.contract.ss2a = 1000
					this.contract.ss2b = 1001
					this.contract.ss2c = 20
					this.contract.ss3a = 1002
					this.contract.ss3c = 20
				}
				this.resetThirdTier()
			},
			/**
			 * Update showSecondTier based on contract changes
			 */
			updateShowSecondTier() {
				this.showSecondTier = this.shouldShowSecondTier()
			}
		}
	}
</script>

<style scoped>
	.hidden {
		display: none;
	}
</style>
